/**
 * @fileoverview Product Analytics - Revenue Attribution
 * @module features/analytics/attribution
 * 
 * @description
 * Multi-touch attribution modeling to understand which marketing
 * channels and touchpoints drive conversions and revenue.
 * 
 * @useCases
 * - Track marketing touchpoints across customer journey
 * - Calculate attribution for conversions
 * - Compare attribution models (first-touch, last-touch, linear)
 * - Optimize marketing spend allocation
 * - Understand channel effectiveness
 * 
 * @metrics
 * - Attributed revenue by channel
 * - Conversion paths
 * - Assisted conversions
 * - Time to conversion
 * - Channel influence score
 * 
 * @chartData
 * - Sankey diagram: Customer journey paths
 * - Bar chart: Revenue by channel
 * - Table: Attribution comparison by model
 * - Pie chart: Channel contribution
 * 
 * @example
 * ```typescript
 * const attribution = new Attribution(webId, endpoint, apiKey);
 * 
 * // Track touchpoint
 * await attribution.trackTouchpoint({
 *   userId: 'user_123',
 *   channel: 'google_ads',
 *   campaign: 'brand_terms',
 *   medium: 'cpc'
 * });
 * ```
 */

import { FlowGridTransport } from "../../client/transport";
import { 
  UTMParams,
  MoneyValue,
  DateRangeFilter,
  TimeSeriesData
} from "../../types";
import { getWindow, getDocument, isBrowser } from "../../utils/browser";

// ============================================================================
// CONFIGURATION TYPES
// ============================================================================

/**
 * Attribution model types.
 */
export type AttributionModel = 
  | 'first_touch'
  | 'last_touch'
  | 'linear'
  | 'time_decay'
  | 'position_based'
  | 'data_driven';

/**
 * Marketing touchpoint configuration.
 */
export interface TouchpointConfig {
  /** User ID */
  userId: string;
  /** Marketing channel */
  channel: string;
  /** Campaign name */
  campaign?: string;
  /** Traffic medium */
  medium?: string;
  /** Traffic source */
  source?: string;
  /** Content/creative */
  content?: string;
  /** Search term */
  term?: string;
  /** Referrer URL */
  referrer?: string;
  /** Landing page */
  landingPage?: string;
  /** Custom properties */
  properties?: Record<string, unknown>;
}

/**
 * Conversion event for attribution.
 */
export interface ConversionEvent {
  /** User ID */
  userId: string;
  /** Conversion type */
  conversionType: 'purchase' | 'signup' | 'lead' | 'custom';
  /** Conversion value */
  value?: MoneyValue;
  /** Conversion identifier (e.g., order ID) */
  conversionId?: string;
  /** Custom properties */
  properties?: Record<string, unknown>;
}

/**
 * Touchpoint response from API.
 */
export interface TouchpointResponse {
  eventName: string;
  properties: Record<string, unknown>;
  response: {
    touchpointId: string;
    userId: string;
    position: number;
    tracked: boolean;
  };
  timestamp: string;
}

/**
 * Attribution report data.
 */
export interface AttributionReportData {
  /** Selected attribution model */
  model: AttributionModel;
  /** Attribution by channel */
  byChannel: Array<{
    channel: string;
    conversions: number;
    attributedRevenue: MoneyValue;
    assistedConversions: number;
    avgTouchpoints: number;
    firstTouchConversions: number;
    lastTouchConversions: number;
  }>;
  /** Top conversion paths */
  topPaths: Array<{
    path: string[];
    conversions: number;
    revenue: MoneyValue;
    avgTimeToConvert: number;
  }>;
  /** Summary metrics */
  summary: {
    totalConversions: number;
    totalRevenue: MoneyValue;
    avgTouchpointsPerConversion: number;
    avgTimeToConversion: number;
    topChannel: string;
    topCampaign: string;
  };
  /** Model comparison */
  modelComparison: Array<{
    model: AttributionModel;
    channelAttribution: Record<string, number>;
  }>;
}

// ============================================================================
// ATTRIBUTION CLASS
// ============================================================================

/**
 * Attribution - Multi-touch attribution tracking.
 * 
 * @extends FlowGridTransport
 * 
 * @description
 * Provides attribution capabilities including:
 * - Touchpoint tracking
 * - Conversion attribution
 * - Multiple attribution models
 * - Path analysis
 * - Channel effectiveness
 * 
 * @example
 * ```typescript
 * const attribution = new Attribution(webId, endpoint, apiKey);
 * 
 * // Track touchpoints throughout customer journey
 * await attribution.trackTouchpoint({
 *   userId: 'user_123',
 *   channel: 'email',
 *   campaign: 'welcome_series',
 *   medium: 'email'
 * });
 * 
 * // Record conversion
 * await attribution.recordConversion({
 *   userId: 'user_123',
 *   conversionType: 'purchase',
 *   value: { amount: 9999, currency: 'USD' },
 *   conversionId: 'ORD-12345'
 * });
 * ```
 */
export class Attribution extends FlowGridTransport {
  /**
   * Tracks a marketing touchpoint.
   * 
   * @param config - Touchpoint configuration
   * @returns Promise resolving to touchpoint response
   * 
   * @example
   * ```typescript
   * await attribution.trackTouchpoint({
   *   userId: 'user_123',
   *   channel: 'facebook_ads',
   *   campaign: 'retargeting_cart',
   *   medium: 'paid_social',
   *   landingPage: '/products/special-offer'
   * });
   * ```
   */
  async trackTouchpoint(config: TouchpointConfig): Promise<TouchpointResponse> {
    return await this.init("attribution_touchpoint", {
      type: "attribution_touchpoint",
      userId: config.userId,
      channel: config.channel,
      campaign: config.campaign,
      medium: config.medium,
      source: config.source,
      content: config.content,
      term: config.term,
      referrer: config.referrer,
      landingPage: config.landingPage,
      ...config.properties,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Records a conversion for attribution.
   * 
   * @param event - Conversion event
   * @returns Promise resolving to conversion response
   * 
   * @example
   * ```typescript
   * await attribution.recordConversion({
   *   userId: 'user_123',
   *   conversionType: 'purchase',
   *   value: { amount: 14999, currency: 'USD' },
   *   conversionId: 'ORD-98765'
   * });
   * ```
   */
  async recordConversion(event: ConversionEvent): Promise<TouchpointResponse> {
    return await this.init("attribution_conversion", {
      type: "attribution_conversion",
      userId: event.userId,
      conversionType: event.conversionType,
      value: event.value,
      conversionId: event.conversionId,
      ...event.properties,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Auto-capture a touchpoint from `utm_*` query params and `document.referrer`.
   * Browser-only; returns null on the server or when no UTM/referrer data is present.
   */
  captureFromUrl(userId: string): TouchpointConfig | null {
    if (!isBrowser()) return null;
    const win = getWindow();
    const doc = getDocument();
    if (!win || !doc) return null;

    const params = new URLSearchParams(win.location.search);
    const utm: UTMParams = {
      utmSource: params.get("utm_source") ?? undefined,
      utmMedium: params.get("utm_medium") ?? undefined,
      utmCampaign: params.get("utm_campaign") ?? undefined,
      utmTerm: params.get("utm_term") ?? undefined,
      utmContent: params.get("utm_content") ?? undefined,
    };
    const referrer = doc.referrer || undefined;

    if (!utm.utmSource && !utm.utmMedium && !utm.utmCampaign && !referrer) {
      return null;
    }

    const config: TouchpointConfig = {
      userId,
      channel: utm.utmSource || (referrer ? "referral" : "direct"),
      campaign: utm.utmCampaign,
      medium: utm.utmMedium,
      source: utm.utmSource,
      content: utm.utmContent,
      term: utm.utmTerm,
      referrer,
      landingPage: win.location.pathname,
    };
    void this.trackTouchpoint(config);
    return config;
  }
}

export default Attribution;
